import { addSettlementAccount, validateNgnAccount } from '@basqet-dashboard/common/api';
import { Button, Dropdown, FormInput, Modal } from '@basqet-dashboard/common/components';
import { ACCOUNT_TYPES } from '@basqet-dashboard/common/config';
import { UserContext } from '@basqet-dashboard/common/context';
import { useCurrencies } from '@basqet-dashboard/common/hooks';
import { useListOfBanks } from '@basqet-dashboard/common/hooks/banks';
import { useFetchSettlementAccounts } from '@basqet-dashboard/common/hooks/queries';
import { addSettlementAccountSchema, formatErrorMessage } from '@basqet-dashboard/common/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';
import React, { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import { EmailVerificationModal } from './modal-email-verification';

const AddSettlementAccountModal = ({ onModalClose }) => {
  const { userHasVerifiedEmail } = useContext(UserContext);
  const { fiatCurrencies, cryptoCurrencies } = useCurrencies();
  const { availableBanks } = useListOfBanks();
  const { refetch: refetchSettlementAccounts } = useFetchSettlementAccounts();
  const [accountValidationError, setAccountValidationError] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setError,
    clearErrors,
    formState: { errors, isSubmitting, isValid, isDirty }
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(addSettlementAccountSchema)
  });

  const _fiatCurrencies = fiatCurrencies.map((currency) => {
    let label;
    if (currency.slug.toUpperCase() === 'USD') {
      label = 'Domicilliary Account (USD)';
    } else if (currency.slug.toUpperCase() === 'NGN') {
      label = 'Naira Account (NGN)';
    } else {
      label = currency.name;
    }
    return { ...currency, label };
  });

  const runAccountValidation = async () => {
    const accountType = watch('type')?.toUpperCase();
    const accountNumber = watch('address');
    const provider = watch('provider');
    const currencyId = watch('currency_id');

    if (accountType !== 'FIAT') {
      clearErrors('address');
      setAccountValidationError(false);
      return true;
    }

    // We are only validating NGN fiat accounts, no support for international bank validation on the backend.
    const selectedCurrency = _fiatCurrencies.find(
      (currency) => String(currency.value) === String(currencyId)
    );
    const isNgn = selectedCurrency.slug.toUpperCase() === 'NGN';

    if (!isNgn) {
      clearErrors('address');
      setAccountValidationError(false);
      return true;
    }

    if (!accountNumber || accountNumber.length < 9) {
      setError('address', { type: 'manual', message: 'Account number is too short' });
      setAccountValidationError(true);
      return false;
    }

    const selectedBank = availableBanks.find((bank) => bank.value === provider);
    const bankCode = selectedBank?.code;

    try {
      const validationResponse = await validateNgnAccount(accountNumber, bankCode);

      if (!validationResponse?.data || !validationResponse.data.valid) {
        setError('address', { type: 'manual', message: 'Account number is invalid' });
        setAccountValidationError(true);
        return false;
      }

      clearErrors('address');
      setAccountValidationError(false);
      return true;
    } catch {
      setError('address', { type: 'manual', message: 'Unable to validate account' });
      setAccountValidationError(true);
      return false;
    }
  };

  const validateBankAccount = async () => {
    await runAccountValidation();
  };

  const validateBankAccountOnSubmit = async (data) => {
    const isValidAccount = await runAccountValidation();
    if (!isValidAccount) return;

    try {
      await addSettlementAccount(data);
      await refetchSettlementAccounts();
      toast('Account Created Successfully');
      onModalClose();
    } catch (error) {
      formatErrorMessage(error);
    }
  };

  const accountType = watch('type')?.toUpperCase();
  const accountIsFiat = accountType === 'FIAT';
  const accountIsCrypto = accountType === 'CRYPTO';
  const fiatCurrencyOptions = accountIsFiat ? _fiatCurrencies : [];

  const currencyProps = {
    'data-testid': 'settlement-currency-select',
    placeholder: accountIsFiat ? 'Bank account type' : 'Currency',
    name: 'currency_id',
    register: register('currency_id'),
    error: errors?.currency_id?.message
  };

  return userHasVerifiedEmail ? (
    <Modal onModalClose={onModalClose} header={{ name: 'Add settlement', cursive: 'account' }}>
      <div className="settlements__form">
        <form
          onSubmit={handleSubmit(validateBankAccountOnSubmit)}
          className="settlements__form__group">
          <Dropdown
            data-testid="settlement-account-type-select"
            placeholder="Account type"
            name="type"
            options={ACCOUNT_TYPES}
            register={register('type')}
            error={errors?.type?.message}
          />

          {accountIsCrypto && <Dropdown options={cryptoCurrencies} {...currencyProps} />}
          {accountIsFiat && (
            <>
              <Dropdown options={fiatCurrencyOptions} {...currencyProps} />
              <Dropdown
                data-testid="settlement-provider-select"
                placeholder="Bank Name"
                name="provider"
                options={availableBanks}
                register={register('provider')}
                error={errors?.provider?.message}
              />
            </>
          )}

          <FormInput
            data-testid="settlement-address-input"
            placeholder={
              !accountType
                ? 'Account number or wallet address'
                : accountIsFiat
                ? 'Account Number'
                : 'Wallet Address'
            }
            name="address"
            type={accountIsFiat ? 'number' : 'text'}
            register={{
              ...register('address'),
              onBlur: validateBankAccount
            }}
            error={errors?.address?.message}
          />

          <Button
            data-testid="add-settlement-account-submit-button"
            isFull
            isLoading={isSubmitting}
            disabled={!isDirty || !isValid || accountValidationError}>
            Add & Save Account
          </Button>
        </form>
      </div>
    </Modal>
  ) : (
    <EmailVerificationModal onModalClose={onModalClose} />
  );
};

AddSettlementAccountModal.propTypes = {
  onModalClose: PropTypes.func
};

export { AddSettlementAccountModal };
